import { create } from 'zustand';

/**
 * Tema tercihi deposu.
 *
 * DIKKAT — depolama bicimi index.html ile SOZLESMELIDIR:
 * localStorage['kural.theme'] DUZ bir string tutar ('system' | 'light' | 'dark').
 * zustand'in `persist` ara katmani kullanilmiyor; o {state:{...},version:0}
 * seklinde bir JSON zarfi yazar ve index.html icindeki FOUC onleyici satir ici
 * betik onu okuyamaz. Betik ilk boyamadan once calisip data-theme'i yazmak
 * zorunda oldugu icin bicimi O belirliyor, depo ona uyuyor.
 */

export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'kural.theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

const isPreference = (value: unknown): value is ThemePreference =>
  value === 'system' || value === 'light' || value === 'dark';

const readStoredPreference = (): ThemePreference => {
  if (typeof window === 'undefined') return 'system';
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isPreference(stored) ? stored : 'system';
  } catch {
    // Gizli sekme / depolama kapali: tercih hatirlanmaz ama uygulama calisir.
    return 'system';
  }
};

const writeStoredPreference = (preference: ThemePreference): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    /* yok sayilir */
  }
};

const systemPrefersDark = (): boolean => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(DARK_QUERY).matches;
};

export const resolveTheme = (preference: ThemePreference): ResolvedTheme => {
  if (preference === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return preference;
};

const applyToDocument = (resolved: ResolvedTheme): void => {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', resolved);
};

type ThemeState = {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  /** Kullanici secimi — kalici olarak yazilir. */
  setPreference: (preference: ThemePreference) => void;
  /** Sistem temasi degistiginde cagrilir (tercih 'system' ise etkilidir). */
  syncWithSystem: () => void;
};

const initialPreference = readStoredPreference();

export const useThemeStore = create<ThemeState>()((set, get) => ({
  preference: initialPreference,
  resolved: resolveTheme(initialPreference),

  setPreference: (preference) => {
    const resolved = resolveTheme(preference);
    writeStoredPreference(preference);
    applyToDocument(resolved);
    set({ preference, resolved });
  },

  syncWithSystem: () => {
    const { preference, resolved } = get();
    if (preference !== 'system') return;
    const next = resolveTheme('system');
    if (next === resolved) return;
    applyToDocument(next);
    set({ resolved: next });
  },
}));

/**
 * Sistem temasi dinleyicisini baglar ve <html data-theme> degerinin depoyla
 * ayni oldugundan emin olur. Uygulama kokunde bir kez cagrilir; donen fonksiyon
 * dinleyiciyi kaldirir.
 */
export const initTheme = (): (() => void) => {
  applyToDocument(useThemeStore.getState().resolved);

  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => undefined;
  }

  const mql = window.matchMedia(DARK_QUERY);
  const onChange = (): void => {
    useThemeStore.getState().syncWithSystem();
  };
  mql.addEventListener('change', onChange);
  return () => {
    mql.removeEventListener('change', onChange);
  };
};

/** Tema secici bilesenleri icin sabit sira — etiketler ceviri ANAHTARI tutar. */
export const THEME_OPTIONS: ReadonlyArray<{
  value: ThemePreference;
  /**
   * `label` DEGIL `labelKey`: modul seviyesinde t() cagirmak metni import
   * aninda dondurur ve dil degisiminde bayat kalir. Ceviri render'da yapilir.
   */
  labelKey: string;
}> = [
  { value: 'system', labelKey: 'profile.themeSystem' },
  { value: 'light', labelKey: 'profile.themeLight' },
  { value: 'dark', labelKey: 'profile.themeDark' },
];
